import { ToastSharedProps } from "@certn/cascadia/components/toast/types";
import { css, keyframes } from "@emotion/css";
import type { AriaToastProps } from "@react-aria/toast";
import { useToast } from "@react-aria/toast";
import { useRef } from "react";

import { CascadiaToast } from "./types";
import { getTextStyle } from "../../foundations/typography/styles";
import { GhostIconButton } from "../button/GhostIconButton";

interface Props extends AriaToastProps<CascadiaToast>, ToastSharedProps {}

export function Toast(props: Props) {
  const { state, ...rest } = props;
  const ref = useRef(null);
  const { toastProps, titleProps, closeButtonProps } = useToast(
    rest,
    state,
    ref
  );

  return (
    <div
      {...toastProps}
      ref={ref}
      className={styles.toast}
      data-toast-type={props.toast.content.type}
      data-animation={props.toast.animation}
      style={{ alignSelf: props.toast.content.position }}
      onAnimationEnd={() => {
        // Remove the toast when the exiting animation completes.
        if (props.toast.animation === "exiting") {
          state.remove(props.toast.key);
        }
      }}
    >
      <div {...titleProps}>{props.toast.content.message}</div>
      <GhostIconButton
        icon="close"
        size="small"
        color="icon-inverse"
        aria-label={closeButtonProps["aria-label"]}
        onPress={() => {
          state.close(props.toast.key);
        }}
      />
    </div>
  );
}

const slideIn = keyframes`
  0% {
    transform: translateY(72px);
    opacity: 0;
  }

  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  0% {
    transform: translateY(0);
    opacity: 1;
  }

  75% {
    transform: translateY(72px);
    opacity: 0;
  }

  100% {
    transform: translateY(72px);
    opacity: 0;
  }
`;

const styles = {
  toast: css(
    {
      display: "flex",
      alignItems: "center",
      gap: "var(--space-3)",
      background: "var(--background-inverse)",
      color: "var(--text-inverse)",
      paddingLeft: "var(--space-4)",
      paddingRight: "var(--space-2)",
      paddingTop: "var(--space-2)",
      paddingBottom: "var(--space-2)",
      borderRadius: "var(--border-radius-2)",
      boxShadow: "var(--shadow-popover)",
      pointerEvents: "auto",
      justifyContent: "space-between",
      maxWidth: "max-content",

      "&[data-animation=entering]": {
        animation: `${slideIn} 300ms ease-out`,
      },

      "&[data-animation=queued]": {
        animation: `${slideIn} 300ms ease-out`,
      },

      "&[data-animation=exiting]": {
        animation: `${slideOut} 400ms ease-in`,
      },

      "&[data-toast-type=error]": {
        backgroundColor: "var(--background-danger-strong)",
      },
    },
    getTextStyle("global/toast")
  ),
};
